/**
 * ImageUploadField — Composant réutilisable upload image + URL
 * 
 * Deux modes: upload direct ou URL externe.
 * Upload prioritaire: si une image est uploadée, elle remplace l'URL.
 * Compatible avec les images existantes par URL.
 */
import React, { useState, useCallback, useRef } from 'react';
import { Upload, Link2, X, Image as ImageIcon, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { API } from '../lib/api';

const MAX_FILE_SIZE_MB = 5;
const ACCEPTED_FORMATS = '.jpg,.jpeg,.png,.webp';
const ACCEPTED_MIME = ['image/jpeg', 'image/png', 'image/webp'];

const ImageUploadField = ({ value, onChange, label = "Image de l'offre", disabled = false }) => {
  const [mode, setMode] = useState(value && !value.includes('/api/uploads/') ? 'url' : 'upload');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleUpload = useCallback(async (file) => {
    setError(null);

    if (!ACCEPTED_MIME.includes(file.type)) {
      setError(`Format non autorisé: ${file.type}. Utilisez JPEG, PNG ou WebP.`);
      return;
    }
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setError(`Fichier trop volumineux (${(file.size / (1024*1024)).toFixed(1)} MB). Maximum: ${MAX_FILE_SIZE_MB} MB.`);
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Erreur upload (${res.status})`);
      }
      const data = await res.json();
      onChange(data.url);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  }, [onChange]);

  const handleFileSelect = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) handleUpload(file);
  }, [handleUpload]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleUpload(file);
  }, [handleUpload]);

  const handleRemove = useCallback(() => {
    onChange('');
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [onChange]);

  return (
    <div className="space-y-3" data-testid="image-upload-field">
      <Label>{label}</Label>

      {/* Mode selector */}
      <div className="flex gap-1 p-0.5 bg-gray-100 rounded-lg w-fit">
        <button
          type="button"
          onClick={() => { setMode('upload'); setError(null); }}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
            mode === 'upload' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}
          data-testid="image-mode-upload"
        >
          <Upload className="h-3.5 w-3.5" /> Importer
        </button>
        <button
          type="button"
          onClick={() => { setMode('url'); setError(null); }}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
            mode === 'url' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}
          data-testid="image-mode-url"
        >
          <Link2 className="h-3.5 w-3.5" /> URL
        </button>
      </div>

      {/* Upload mode */}
      {mode === 'upload' && (
        <div
          className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
            dragOver ? 'border-blue-400 bg-blue-50' :
            disabled ? 'border-gray-200 bg-gray-50' :
            'border-gray-300 hover:border-gray-400 bg-white'
          }`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          data-testid="image-drop-zone"
        >
          {uploading ? (
            <div className="flex flex-col items-center gap-2 py-2">
              <Loader2 className="h-6 w-6 text-blue-500 animate-spin" />
              <span className="text-sm text-gray-500">Upload en cours...</span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 py-2">
              <ImageIcon className="h-8 w-8 text-gray-400" />
              <p className="text-sm text-gray-500">
                Glissez une image ici ou{' '}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="text-blue-600 hover:text-blue-700 font-medium underline"
                  disabled={disabled}
                >
                  parcourir
                </button>
              </p>
              <p className="text-xs text-gray-400">JPEG, PNG, WebP — Max {MAX_FILE_SIZE_MB} MB</p>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_FORMATS}
            onChange={handleFileSelect}
            className="hidden"
            disabled={disabled || uploading}
            data-testid="image-file-input"
            aria-label="Sélectionner une image"
          />
        </div>
      )}

      {/* URL mode */}
      {mode === 'url' && (
        <Input
          value={value || ''}
          onChange={(e) => { onChange(e.target.value); setError(null); }}
          placeholder="https://..."
          disabled={disabled}
          data-testid="image-url-input"
          aria-label="URL de l'image"
        />
      )}

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2" role="alert" data-testid="image-upload-error">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Preview */}
      {value && (
        <div className="relative inline-block" data-testid="image-preview-container">
          <div className="relative w-full max-w-xs rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
            <img
              src={value}
              alt="Aperçu"
              className="w-full h-auto max-h-40 object-contain"
              onError={(e) => { e.target.style.display = 'none'; }}
              data-testid="image-preview"
            />
          </div>
          {!disabled && (
            <Button
              type="button"
              variant="destructive"
              size="sm"
              className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full shadow-md"
              onClick={handleRemove}
              data-testid="image-remove-button"
              title="Supprimer l'image"
              aria-label="Supprimer l'image"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default ImageUploadField;
